import { OASElement } from '@oas-ui/core'
import { pickOnColor as pickOnColorBase } from '../../shared/on-color.js'
import { lookupIcon } from '@oas-ui/icons/runtime'
import { normalizeSize, THREE_SIZES } from '../../shared/size.js'

export type ToggleButtonSize = 'small' | 'medium' | 'large'
export type ToggleButtonStatus = 'success' | 'warning' | 'error'
/** variant 形态（对齐 button variant 体系，取切换语义有意义的子集）：solid 实底 / outlined 描边 / filled 浅底 / text 纯文字 */
const VALID_VARIANTS = ['solid', 'outlined', 'filled', 'text'] as const

const VALID_STATUSES: readonly ToggleButtonStatus[] = ['success', 'warning', 'error']
/** 预设色板名（ui-spec §4.1 color 协议，映射 --oas-preset-* token） */
const PRESET_COLORS: readonly string[] = [
  'magenta',
  'red',
  'volcano',
  'orange',
  'gold',
  'lime',
  'green',
  'cyan',
  'blue',
  'geekblue',
  'purple',
]

function normalizeChoice(raw: string, fallback: string, valid: readonly string[]): string {
  return (valid as readonly string[]).includes(raw) ? raw : fallback
}

/**
 * 自定义选中色的实底文字色：按相对亮度取深/浅，保证对比可读（共享实现见 `shared/on-color`）。
 */
function pickOnColor(color: string): string {
  return pickOnColorBase(color, { dark: '#18181b', light: '#ffffff', fallback: '' })
}

const STYLE = `
:host {
  display: inline-block;
  font-family: inherit;
}
:host([hidden]) {
  display: none;
}
button {
  appearance: none;
  box-sizing: border-box;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-height: var(--oas-control-height-md);
  padding: 0 var(--oas-space-4);
  border: 1px solid var(--oas-color-border);
  border-radius: var(--oas-radius-md);
  background: var(--oas-color-bg);
  color: var(--oas-color-text-primary);
  font-size: var(--oas-font-size-md);
  font-family: inherit;
  cursor: pointer;
  transition: background var(--oas-transition-fast) var(--oas-ease-out),
    border-color var(--oas-transition-fast) var(--oas-ease-out),
    color var(--oas-transition-fast) var(--oas-ease-out);
}
button:hover {
  border-color: var(--oas-toggle-color, var(--oas-color-primary));
}
button:focus-visible {
  outline: none;
  box-shadow: var(--oas-focus-ring);
}
button[aria-pressed='true'] {
  background: var(--oas-toggle-color, var(--oas-color-primary));
  border-color: var(--oas-toggle-color, var(--oas-color-primary));
  color: var(--oas-toggle-on-color, var(--oas-color-text-on-primary));
}
button[disabled] {
  cursor: not-allowed;
  background: var(--oas-color-bg-disabled);
  color: var(--oas-color-text-disabled);
  border-color: var(--oas-color-border);
}
/* ---- 图标：查表所得内联 SVG（跟随 currentColor，装饰性对读屏隐藏） ---- */
button.has-icon {
  gap: var(--oas-space-2);
}
button .icon {
  display: inline-flex;
}
button.icon-only {
  aspect-ratio: 1;
  padding: 0;
}
/* ---- size 尺寸档（默认 medium 走基础样式；控高对齐 control-height token） ---- */
:host([data-size='small']) button {
  min-height: var(--oas-control-height-sm);
  font-size: var(--oas-font-size-sm);
  padding-inline: var(--oas-space-3);
}
:host([data-size='large']) button {
  min-height: var(--oas-control-height-lg);
  font-size: var(--oas-font-size-lg);
  padding-inline: var(--oas-space-5);
}
/* ===== variant 形态维度（正交 pressed 选中态与 status 语义色；status 规则置于其后按源顺序胜出）=====
   solid 为默认不加 data-variant 形态样式；outlined 透明底描边、filled 浅底无描边、text 无框无底 */
:host([data-variant='outlined']) button {
  background: transparent;
}
:host([data-variant='outlined']) button:hover:not([disabled]) {
  background: color-mix(in srgb, var(--oas-toggle-color, var(--oas-color-primary)) 8%, transparent);
}
:host([data-variant='outlined']) button[aria-pressed='true'] {
  background: color-mix(in srgb, var(--oas-toggle-color, var(--oas-color-primary)) 10%, transparent);
  border-color: var(--oas-toggle-color, var(--oas-color-primary));
  /* 选中文字落主色淡底：纯主色感知分值 <60（同色系低明度差）——改用项目「主色文字安全档」
     （light=primary 75%+black / dark=primary 78%+white 主题感知档）；自定义色可经 --oas-toggle-tint-text 覆盖 */
  color: var(--oas-toggle-tint-text, var(--oas-color-primary-text));
}
:host([data-variant='filled']) button {
  background: color-mix(in srgb, var(--oas-toggle-color, var(--oas-color-primary)) 12%, transparent);
  border-color: transparent;
}
:host([data-variant='filled']) button:hover:not([disabled]) {
  background: color-mix(in srgb, var(--oas-toggle-color, var(--oas-color-primary)) 18%, transparent);
}
:host([data-variant='filled']) button[aria-pressed='true'] {
  background: var(--oas-toggle-color, var(--oas-color-primary));
  border-color: var(--oas-toggle-color, var(--oas-color-primary));
  color: var(--oas-toggle-on-color, var(--oas-color-text-on-primary));
}
:host([data-variant='text']) button {
  background: transparent;
  border-color: transparent;
}
:host([data-variant='text']) button:hover:not([disabled]) {
  background: color-mix(in srgb, var(--oas-toggle-color, var(--oas-color-primary)) 8%, transparent);
}
:host([data-variant='text']) button[aria-pressed='true'] {
  background: color-mix(in srgb, var(--oas-toggle-color, var(--oas-color-primary)) 12%, transparent);
  /* 同 outlined：淡底上改用主色文字安全档达标（自定义色可经 --oas-toggle-tint-text 覆盖） */
  color: var(--oas-toggle-tint-text, var(--oas-color-primary-text));
}
/* variant 系列禁用态回落统一禁用视觉（不被形态底色覆盖） */
:host([data-variant='outlined']) button[disabled],
:host([data-variant='filled']) button[disabled],
:host([data-variant='text']) button[disabled] {
  background: var(--oas-color-bg-disabled);
  border-color: var(--oas-color-border);
  color: var(--oas-color-text-disabled);
}
/* ---- status 校验态：success / warning / error（宿主自设 aria-invalid 等效 error，置于最后统一胜出） ---- */
:host([data-status='success']) button {
  border-color: var(--oas-color-success);
}
:host([data-status='success']) button:focus-visible {
  box-shadow: 0 0 0 2px color-mix(in srgb, var(--oas-color-success) 30%, transparent);
}
:host([data-status='success']) button[aria-pressed='true'] {
  background: var(--oas-color-success);
  border-color: var(--oas-color-success);
  color: var(--oas-color-text-on-success);
}
:host([data-status='warning']) button {
  border-color: var(--oas-color-warning);
}
:host([data-status='warning']) button:focus-visible {
  box-shadow: 0 0 0 2px color-mix(in srgb, var(--oas-color-warning) 30%, transparent);
}
:host([data-status='warning']) button[aria-pressed='true'] {
  background: var(--oas-color-warning);
  border-color: var(--oas-color-warning);
  color: var(--oas-color-text-on-warning);
}
:host([data-status='error']) button,
:host([aria-invalid='true']) button {
  border-color: var(--oas-color-danger);
}
:host([data-status='error']) button:focus-visible,
:host([aria-invalid='true']) button:focus-visible {
  box-shadow: 0 0 0 2px color-mix(in srgb, var(--oas-color-danger) 30%, transparent);
}
:host([data-status='error']) button[aria-pressed='true'],
:host([aria-invalid='true']) button[aria-pressed='true'] {
  background: var(--oas-color-danger);
  border-color: var(--oas-color-danger);
  color: var(--oas-color-text-on-danger);
}
`

export class OASToggleButton extends OASElement {
  static override get observedAttributes(): string[] {
    return [
      'value',
      'pressed',
      'disabled',
      'disabled-skip',
      'size',
      'icon',
      'icon-toggled',
      'color',
      'status',
      'variant',
      'aria-label',
    ]
  }

  private btn: HTMLButtonElement | null = null
  /** aria-invalid 由 status=error 联动写入的所有权标志：清理时只移除组件设置的，不动宿主自设 */
  private invalidByStatus = false

  /**
   * @apiProperty 当前值（值标识，随 oas-change 回传）：即 `value` 属性字符串；按下态是独立
   * 的 `pressed` 受控属性，不是本 property 语义（与 checkbox 组内「value = 选项标识」一致）。
   */
  get value(): string {
    return this.getAttr('value', '')
  }

  /**
   * 程序性写值（受控赋值即生效语义）：写受控 `value` 属性（值标识）并强制 update 同步内部
   * 按钮状态，不派发任何事件。同值 `setAttribute` 不触发 attributeChangedCallback，显式
   * update 保证生效。
   */
  set value(v: string) {
    this.setAttribute('value', v == null ? '' : String(v))
    if (this.hasRendered) this.update()
  }

  /** 纯函数：SSR 快照与客户端渲染共用同一份模板，保证两路径结构严格一致 */
  private template(): string {
    return `
      <style>${STYLE}</style>
      <button part="button" type="button" role="button" aria-pressed="false">
        <span class="icon" part="icon" aria-hidden="true" hidden></span>
        <slot></slot>
      </button>
    `
  }

  /** 缓存节点引用 + 绑定切换事件（render 与水合路径共用） */
  private bind(): void {
    this.btn = this.shadow.querySelector('button')
    this.btn?.addEventListener('click', () => this.toggle())
  }

  protected override render(): void {
    this.shadow.innerHTML = this.template()
    this.bind()
    this.update()
  }

  /** 真水合：校验 SSR 快照结构（button 存在）后直接接管，跳过 shadow 重建 */
  protected override hydrate(): boolean {
    if (!this.shadow.querySelector('button[part="button"]')) return false
    this.bind()
    return true
  }

  protected override update(): void {
    const btn = this.btn
    if (!btn) return
    const pressed = this.hasAttr('pressed')
    btn.setAttribute('aria-pressed', String(pressed))
    // disabled 就近读取全局禁用注入（组件显式 disabled > 豁免 > provider 注入；
    // 与 toggle-group/oas-button 同通道，不再只读自身属性漏掉 config-provider 注入）
    btn.disabled = this.hasAttr('disabled') || this.injectDisabled()
    this.syncSizeStatus()
    this.syncIcon()
    this.syncColor()
  }

  /** size/status/variant 镜像到宿主 data-*（供 :host([data-*]) 样式消费）；error 联动 aria-invalid */
  private syncSizeStatus(): void {
    const size = normalizeSize(this.injectValue('size', 'medium'), THREE_SIZES, 'medium')
    this.setAttribute('data-size', size)
    // variant 形态镜像（solid/outlined/filled/text；非法值静默回落 solid）
    this.setAttribute('data-variant', normalizeChoice(this.getAttr('variant', ''), 'solid', VALID_VARIANTS))
    const status = normalizeChoice(this.getAttr('status', ''), '', VALID_STATUSES)
    if (status) this.setAttribute('data-status', status)
    else this.removeAttribute('data-status')
    if (status === 'error') {
      if (!this.hasAttribute('aria-invalid')) this.invalidByStatus = true
      this.setAttribute('aria-invalid', 'true')
    } else if (this.invalidByStatus) {
      this.invalidByStatus = false
      this.removeAttribute('aria-invalid')
    }
  }

  /** 图标渲染与可访问名称：宿主 aria-label 优先；纯图标（无文字）时以图标名兜底。
   *  双态图标：按下态优先用 `icon-toggled`（未设或未按下回落 `icon`；非法名同 icon 不渲染） */
  private syncIcon(): void {
    const btn = this.btn
    if (!btn) return
    const pressed = this.hasAttr('pressed')
    const toggled = this.getAttr('icon-toggled', '')
    const icon = pressed && toggled ? toggled : this.getAttr('icon', '')
    const content = icon ? lookupIcon(icon) : undefined
    const hasIcon = content !== undefined
    const hasText = (this.textContent ?? '').trim().length > 0
    const iconOnly = hasIcon && !hasText
    btn.className = [hasIcon ? 'has-icon' : '', iconOnly ? 'icon-only' : ''].filter(Boolean).join(' ')
    const iconEl = this.shadow.querySelector<HTMLElement>('.icon')
    if (iconEl) {
      iconEl.hidden = !hasIcon
      iconEl.textContent = ''
      if (content) {
        const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg')
        svg.setAttribute('viewBox', '0 0 16 16')
        svg.setAttribute('width', '1em')
        svg.setAttribute('height', '1em')
        svg.setAttribute('aria-hidden', 'true')
        svg.setAttribute('focusable', 'false')
        svg.innerHTML = content
        iconEl.appendChild(svg)
      }
    }
    const hostLabel = this.getAttribute('aria-label')
    if (hostLabel) btn.setAttribute('aria-label', hostLabel)
    else if (iconOnly) btn.setAttribute('aria-label', icon)
    else btn.removeAttribute('aria-label')
  }

  /** color 选中色（ui-spec §4.1 三级协议）：预设名 → preset token；字面色直接注入并计算实底文字色。
   *  感知对比度（≥60）：预设基色（如 green #52c41a）对白字不达标，选中实底用更深的 -text 档 */
  private syncColor(): void {
    const color = this.getAttr('color', '')
    if (color) {
      const isPreset = (PRESET_COLORS as readonly string[]).includes(color)
      const base = isPreset ? `var(--oas-preset-${color}-text)` : color
      this.style.setProperty('--oas-toggle-color', base)
      // 预设名/变量写法无法在 JS 侧算亮度，交由 CSS 的 text-on-primary 兜底；字面量才算
      const onColor = isPreset ? '' : pickOnColor(color)
      if (onColor) this.style.setProperty('--oas-toggle-on-color', onColor)
      else this.style.removeProperty('--oas-toggle-on-color')
    } else {
      this.style.removeProperty('--oas-toggle-color')
      this.style.removeProperty('--oas-toggle-on-color')
    }
  }

  private toggle(): void {
    if (this.hasAttr('disabled') || this.injectDisabled()) return
    const pressed = !this.hasAttr('pressed')
    this.toggleAttribute('pressed', pressed)
    this.emit('change', { value: this.getAttr('value', ''), pressed })
  }
}
